/**
 * Payment voucher — a branded, print-ready A5 *landscape* receipt for a single
 * payment: سند قبض (inbound) / سند دفع (outbound). Mirrors the journal-voucher
 * aesthetic (white paper, brand band from the active palette) but is laid out
 * as a classic cash voucher: who paid / who was paid, the amount writ large,
 * the amount in words, which invoices/bills the money settled, and signature
 * slots. WhatsApp send uses the tenant's existing `payment` template.
 *
 * No app shell: a slim viewer chrome (back · WhatsApp · print) over the paper.
 * `@page { size: A5 landscape }` so it lands on one A5 sheet.
 */

import { ArrowLeft, Printer } from 'lucide-react';
import { type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { amountInWords } from '../../lib/amountInWords.js';
import { api } from '../../lib/api.js';
import { getBrand } from '../../lib/brandClient.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { PALETTES, loadPalette, swatchAccent } from '../../lib/palette.js';
import { WhatsAppButton } from '../common/WhatsAppButton.js';

interface PaymentAllocation {
  id: string;
  targetType: string;
  targetId: string;
  /** How much of THIS payment went to this document. */
  amount: string;
  docNumber: string | null;
  docDate: string | null;
  /** The settled document's own position — what it is worth, and what is left. */
  docTotal: string | null;
  docPaid: string | null;
  docRemaining: string | null;
}

interface PaymentDoc {
  id: string;
  docNumber: string;
  paymentDate: string;
  direction: 'inbound' | 'outbound';
  partyId: string | null;
  status: 'draft' | 'posted' | 'voided';
  currencyCode: string;
  amount: string;
  method: string | null;
  reference: string | null;
  notes: string | null;
  postedAt: string | null;
  allocations?: PaymentAllocation[];
  /** Of `amount`, how much was pinned to documents vs left sitting on account. */
  allocatedAmount?: string;
  unallocatedAmount?: string;
  /** What this party still owes across their whole account, per currency. */
  partyBalance?: Array<{ currencyCode: string; amount: string }>;
  /** The party's OTHER documents still open in this currency, oldest first. */
  otherOpenDocs?: Array<Omit<PaymentAllocation, 'id' | 'amount'>>;
  /** Their money on account in this currency (a minus = money given to them). */
  onAccountCredit?: string;
}

/** Past this many, the rest of the open invoices print as one line — still in the total.
 *  Kept low so the paper still reads at A5 once it is fitted to one page. */
const MAX_OTHER_ROWS = 12;

/** A5 landscape in CSS px (96/in): 210 × 148 mm. */
const PAGE_W = (210 / 25.4) * 96;
/** The height the paper must fit in — a hair under the page, for printer rounding. */
const PAGE_H = ((148 / 25.4) * 96) * 0.97;

/**
 * How much the paper must shrink to print on ONE page (owner, 2026-11-01: a
 * receipt listing the account's open invoices ran onto a second sheet). Measured
 * at the printed width, without the on-screen minimum height; 1 = it fits.
 * Applied only in print (`.a5[data-fit]` below), so the screen stays full size.
 */
function fitToPage(el: HTMLElement | null): void {
  if (!el) return;
  const prev = { width: el.style.width, maxWidth: el.style.maxWidth, minHeight: el.style.minHeight };
  el.style.width = `${PAGE_W}px`;
  el.style.maxWidth = 'none';
  el.style.minHeight = '0';
  const need = el.scrollHeight;
  Object.assign(el.style, prev);
  if (need > PAGE_H) {
    el.dataset.fit = '';
    el.style.setProperty('--fit', (PAGE_H / need).toFixed(4));
  } else {
    delete el.dataset.fit;
    el.style.removeProperty('--fit');
  }
}

export function PaymentVoucherPrintPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('documents');
  const { t: tc } = useTranslation();
  const brand = getBrand();
  const [payment, setPayment] = useState<PaymentDoc | null>(null);
  const [party, setParty] = useState<{ displayName: string; phone: string | null } | null>(null);
  const [tenant, setTenant] = useState<{ name: string } | null>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const { paperBrand, paperAccent } = useMemo(() => {
    const p = PALETTES.find((x) => x.id === loadPalette()) ?? PALETTES[0]!;
    return { paperBrand: p.primary, paperAccent: swatchAccent(p) };
  }, []);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const r = await api().get<PaymentDoc>(`/payments/${id}`);
      setPayment(r.data);
      const tn = await api().get<{ name: string }>('/tenants/current');
      setTenant({ name: tn.data.name });
      if (r.data.partyId) {
        // Best-effort: a voucher without the phone still prints.
        try {
          const p = await api().get<{ displayName: string; phone: string | null }>(`/parties/${r.data.partyId}`);
          setParty({ displayName: p.data.displayName, phone: p.data.phone });
        } catch { /* party read not permitted — name stays blank */ }
      }
    })();
  }, [id]);

  const locale = i18n.language;
  const isAr = i18n.dir(locale) === 'rtl';

  // One printed page, always: measured whenever the paper changes, and again
  // right before printing (the print button and Ctrl+P both fire beforeprint).
  useLayoutEffect(() => { fitToPage(paperRef.current); }, [payment, party, tenant, locale]);
  useEffect(() => {
    const onBefore = (): void => fitToPage(paperRef.current);
    window.addEventListener('beforeprint', onBefore);
    return () => window.removeEventListener('beforeprint', onBefore);
  }, []);

  if (!payment) {
    return <p className="p-6 text-sm text-fg-muted">{tc('actions.loading', { defaultValue: 'Loading…' })}</p>;
  }

  const inbound = payment.direction === 'inbound';
  // Which way the money went: a MINUS receipt is money handed back to the
  // customer (and a minus supplier payment, money they gave back) — the paper
  // says so instead of printing a receipt for "-1,000".
  const moneyIn = inbound !== Number(payment.amount) < 0;
  const amountAbs = Math.abs(Number(payment.amount)).toFixed(4);
  const m = (amount: string | number): string =>
    formatMoney(typeof amount === 'number' ? amount.toFixed(4) : amount, { locale, currency: payment.currencyCode });

  const title = moneyIn
    ? t('payments.voucher.receiptTitle', { defaultValue: 'Receipt Voucher' })
    : t('payments.voucher.paymentTitle', { defaultValue: 'Payment Voucher' });
  // Cash is the normal case — an empty method column means cash, not "unknown".
  const methodKey = (payment.method ?? 'cash').toLowerCase();
  const methodLabel = t(`payments.voucher.method.${methodKey}`, { defaultValue: payment.method ?? t('payments.voucher.method.cash', { defaultValue: 'Cash' }) });
  const sellerName = tenant?.name ?? brand.name.full;
  const sellerSub = isAr ? brand.name.tagline_ar : brand.name.tagline_en;
  const allocations = payment.allocations ?? [];

  /**
   * The number the person holding this paper came to hear: what they still owe
   * across their whole account, in the currency they just paid in. The server
   * only sends the currencies with something open in them, so "no row" is the
   * answer "nothing left" — printed as a zero and labelled, never as a blank.
   */
  const balance = payment.partyBalance?.find((b) => b.currencyCode === payment.currencyCode)?.amount ?? '0';
  const settled = Number(balance) <= 0.00005;
  /** Other currencies they are still behind in — named so the zero above can't mislead. */
  const otherBalances = (payment.partyBalance ?? []).filter((b) => b.currencyCode !== payment.currencyCode);
  const onAccount = Number(payment.unallocatedAmount ?? '0');
  const allocatedTotal = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  /**
   * The rest of the account under what this payment settled: every other
   * invoice still open in this currency, and their money on account. The
   * «المتبقي» total is then the WHOLE account's — the balance box's figure —
   * not just the invoice the money landed on (owner, 2026-11-01).
   */
  const others = payment.otherOpenDocs ?? [];
  const shownOthers = others.slice(0, MAX_OTHER_ROWS);
  const hiddenOthers = others.slice(MAX_OTHER_ROWS);
  const hiddenLeft = hiddenOthers.reduce((s, d) => s + Number(d.docRemaining || 0), 0);
  const credit = Number(payment.onAccountCredit ?? 0);
  const hasCredit = Math.abs(credit) > 0.00005;
  const wholeAccount = others.length > 0 || hasCredit;
  const openTotal = allocations.reduce((s, a) => s + Number(a.docRemaining || 0), 0)
    + others.reduce((s, d) => s + Number(d.docRemaining || 0), 0)
    - credit;
  // Only worth a column each once the server is actually sending the figures —
  // a browser against an older API keeps the plain two-column table.
  const showDocFigures = allocations.some((a) => a.docTotal !== null && a.docTotal !== undefined) || wholeAccount;
  const cols = showDocFigures ? 6 : 3;
  const docLabel = (type: string): string => (type === 'invoice'
    ? t('payments.voucher.invoice', { defaultValue: 'Invoice' })
    : t('payments.voucher.bill', { defaultValue: 'Bill' }));

  const statusChip = payment.status === 'posted'
    ? { cls: 'ok', label: t('payments.status.posted', { defaultValue: 'Posted' }) }
    : payment.status === 'voided'
      ? { cls: 'bad', label: t('payments.status.voided', { defaultValue: 'Voided' }) }
      : { cls: 'mid', label: t('payments.status.draft', { defaultValue: 'Draft' }) };

  const rootStyle = { '--paper-brand': paperBrand, '--paper-accent': paperAccent } as CSSProperties;

  return (
    <div className="pv" dir={i18n.dir(locale)} style={rootStyle}>
      <style>{CSS}</style>
      <style media="print">{'@page{size:A5 landscape;margin:0}'}</style>

      {/* viewer chrome */}
      <header className="pv-bar">
        <Link to="/payments" className="pv-back">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          <span>{tc('actions.back', { defaultValue: 'Back' })}</span>
        </Link>
        <span className="pv-title">{title} · <span className="font-mono">{payment.docNumber}</span></span>
        <span className="pv-spacer" />
        <WhatsAppButton
          size="sm"
          docType="payment"
          phone={party?.phone ?? null}
          vars={{
            company: sellerName,
            customer: party?.displayName ?? '',
            docNumber: payment.docNumber,
            date: formatDate(payment.paymentDate, { locale }),
            total: m(amountAbs),
            currency: payment.currencyCode,
          }}
        />
        <button type="button" className="pv-print" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden />
          <span>{tc('actions.print', { defaultValue: 'Print' })}</span>
        </button>
      </header>

      <div className="pv-stage">
        <div className="a5" ref={paperRef}>
          {payment.status === 'voided' && (
            <div className="stamp">{t('payments.status.voided', { defaultValue: 'Voided' })}</div>
          )}

          {/* header band */}
          <div className="pv-top">
            <div className="brand">
              <div className="mk" aria-hidden>{brand.name.short?.slice(0, 2) || 'IQ'}</div>
              <div>
                <div className="co">{sellerName}</div>
                {sellerSub && <div className="cosub">{sellerSub}</div>}
              </div>
            </div>
            <div className="doc">
              <div className="dt">{title}</div>
              <div className="dn">{payment.docNumber}</div>
            </div>
          </div>

          {/* meta strip */}
          <div className="pv-meta">
            <div className="mi"><div className="k">{t('payments.voucher.date', { defaultValue: 'Date' })}</div><div className="v">{formatDate(payment.paymentDate, { locale })}</div></div>
            <div className="mi"><div className="k">{t('payments.voucher.methodLabel', { defaultValue: 'Payment method' })}</div><div className="v">{methodLabel}</div></div>
            {payment.reference && (
              <div className="mi"><div className="k">{t('payments.voucher.reference', { defaultValue: 'Reference' })}</div><div className="v">{payment.reference}</div></div>
            )}
            <span className={`chip ${statusChip.cls}`}>{statusChip.label}</span>
          </div>

          <div className="pv-body">
            {/* who + how much */}
            <div className="pv-main">
              <div className="who">
                <div className="k">
                  {moneyIn
                    ? t('payments.voucher.receivedFrom', { defaultValue: 'Received from' })
                    : t('payments.voucher.paidTo', { defaultValue: 'Paid to' })}
                </div>
                <div className="v">{party?.displayName ?? '—'}</div>
                {party?.phone && <div className="phone" dir="ltr">{party.phone}</div>}
                <div className="k words-k">{t('payments.voucher.amountInWords', { defaultValue: 'Amount in words' })}</div>
                <div className="words-v">{amountInWords(Number(amountAbs), payment.currencyCode, locale)}</div>
              </div>
              {/* Paid now, and what is still owed after it — the two figures the
                  counter is asked for, side by side so neither can be missed. */}
              <div className="money">
                <div className="amount">
                  <div className="k">{t('payments.voucher.amount', { defaultValue: 'Amount' })}</div>
                  <div className="big">{m(amountAbs)}</div>
                  {onAccount > 0 && (
                    <div className="sub">
                      {t('payments.voucher.onAccount', { defaultValue: 'On account' })}: {m(payment.unallocatedAmount ?? '0')}
                    </div>
                  )}
                </div>
                {/* Only where there IS an account. A counter sale taken without
                    naming a party has nobody to owe anything, and printing
                    "remaining: 0 — settled in full" on it would be a claim
                    about a customer who does not exist. */}
                {payment.partyId && (
                  <div className={`balance ${settled ? 'clear' : 'due'}`}>
                    <div className="k">
                      {inbound
                        ? t('payments.voucher.balanceRemaining', { defaultValue: 'Remaining balance' })
                        : t('payments.voucher.balanceOwedToThem', { defaultValue: 'Still owed to them' })}
                    </div>
                    <div className="big">{m(balance)}</div>
                    <div className="sub">
                      {settled
                        ? t('payments.voucher.settledInFull', { defaultValue: 'Account settled in full' })
                        : otherBalances.length > 0
                          ? otherBalances.map((b) => formatMoney(b.amount, { locale, currency: b.currencyCode })).join(' · ')
                          : t('payments.voucher.balanceHint', { defaultValue: 'Across the whole account' })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* what it settled — and what is still open on the rest of the account */}
            {(allocations.length > 0 || wholeAccount) && (
              <table className={`allocs${allocations.length + shownOthers.length > 8 ? ' many' : ''}`}>
                <thead>
                  <tr>
                    <th className="idx">#</th>
                    <th className="l">{t('payments.voucher.settles', { defaultValue: 'In settlement of' })}</th>
                    {showDocFigures && <th>{t('payments.voucher.docTotal', { defaultValue: 'Total' })}</th>}
                    <th>{t('payments.voucher.applied', { defaultValue: 'Applied' })}</th>
                    {showDocFigures && <th>{tc('documents:columnsShared.paid', { defaultValue: 'Paid' })}</th>}
                    {showDocFigures && <th>{tc('documents:columnsShared.remaining', { defaultValue: 'Remaining' })}</th>}
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((a, i) => {
                    const left = Number(a.docRemaining ?? 0);
                    return (
                      <tr key={a.id}>
                        <td className="idx">{i + 1}</td>
                        <td className="l">
                          <span className="acode">{a.docNumber ?? a.targetId.slice(0, 8)}</span>
                          <span className="aname">{docLabel(a.targetType)}</span>
                          {a.docDate && <span className="adate">{formatDate(a.docDate, { locale })}</span>}
                        </td>
                        {showDocFigures && <td className="dim">{a.docTotal ? m(a.docTotal) : '—'}</td>}
                        <td className="amt">{m(a.amount)}</td>
                        {showDocFigures && <td className="dim">{a.docPaid ? m(a.docPaid) : '—'}</td>}
                        {showDocFigures && (
                          <td className={left > 0.00005 ? 'due' : 'clear'}>
                            {a.docRemaining ? m(a.docRemaining) : '—'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {shownOthers.length > 0 && (
                    <tr className="grp">
                      <td colSpan={cols}>{t('payments.voucher.otherOpen', { defaultValue: 'Other invoices still open' })}</td>
                    </tr>
                  )}
                  {shownOthers.map((d, i) => (
                    <tr key={`${d.targetType}:${d.targetId}`}>
                      <td className="idx">{allocations.length + i + 1}</td>
                      <td className="l">
                        <span className="acode">{d.docNumber ?? d.targetId.slice(0, 8)}</span>
                        <span className="aname">{docLabel(d.targetType)}</span>
                        {d.docDate && <span className="adate">{formatDate(d.docDate, { locale })}</span>}
                      </td>
                      <td className="dim">{d.docTotal ? m(d.docTotal) : '—'}</td>
                      <td className="dim">—</td>
                      <td className="dim">{d.docPaid ? m(d.docPaid) : '—'}</td>
                      <td className="due">{d.docRemaining ? m(d.docRemaining) : '—'}</td>
                    </tr>
                  ))}
                  {hiddenOthers.length > 0 && (
                    <tr>
                      <td className="idx" />
                      <td className="l aname">{t('payments.voucher.moreDocs', { defaultValue: '+ {{n}} more invoices', n: hiddenOthers.length })}</td>
                      <td /><td /><td />
                      <td className="due">{m(hiddenLeft.toFixed(4))}</td>
                    </tr>
                  )}
                  {/* Their money on account already comes off what they owe — the
                      row that makes the total below equal the balance box. */}
                  {hasCredit && (
                    <tr>
                      <td className="idx" />
                      <td className="l aname">
                        {credit > 0
                          ? t('payments.voucher.creditOnAccount', { defaultValue: 'Paid on account, not yet applied' })
                          : t('payments.voucher.givenOnAccount', { defaultValue: 'Given to them on account' })}
                      </td>
                      {showDocFigures && <td />}
                      <td />
                      {showDocFigures && <td />}
                      {showDocFigures && <td className={credit > 0 ? 'clear' : 'due'}>{m((-credit).toFixed(4))}</td>}
                    </tr>
                  )}
                </tbody>
                {/* One line that adds it up, so nobody has to. */}
                <tfoot>
                  <tr>
                    <td className="idx" />
                    <td className="l">
                      {wholeAccount
                        ? t('payments.voucher.totalsAll', { defaultValue: 'Total — whole account' })
                        : t('payments.voucher.totalSettled', { defaultValue: 'Total settled' })}
                    </td>
                    {showDocFigures && <td />}
                    <td className="amt">{m(allocatedTotal.toFixed(4))}</td>
                    {showDocFigures && <td />}
                    {showDocFigures && (
                      <td className={openTotal > 0.00005 ? 'due' : 'clear'}>{m(Math.max(openTotal, 0).toFixed(4))}</td>
                    )}
                  </tr>
                </tfoot>
              </table>
            )}

            {/* No allocations at all: a deposit against the account rather than a
                receipt for nothing. Say which, or the paper reads as incomplete. */}
            {allocations.length === 0 && (
              <div className="blk">
                <div className="k">{t('payments.voucher.settles', { defaultValue: 'In settlement of' })}</div>
                <div className="v">{t('payments.voucher.onAccountOnly', { defaultValue: 'On account — not applied to a specific document.' })}</div>
              </div>
            )}

            {payment.notes && (
              <div className="blk">
                <div className="k">{t('payments.voucher.notes', { defaultValue: 'Notes' })}</div>
                <div className="v">{payment.notes}</div>
              </div>
            )}
          </div>

          {/* footer: signatures */}
          <div className="pv-foot">
            <div className="sigs">
              <div className="sig"><div className="line">{t('payments.voucher.preparedBy', { defaultValue: 'Prepared by' })}</div></div>
              <div className="sig">
                <div className="line">
                  {moneyIn
                    ? t('payments.voucher.receivedBy', { defaultValue: 'Received by' })
                    : t('payments.voucher.paidBy', { defaultValue: 'Paid by' })}
                </div>
              </div>
              <div className="sig"><div className="line">{t('payments.voucher.customerSig', { defaultValue: 'Second party' })}</div></div>
            </div>
            <div className="postline">
              <span>{sellerName}</span>
              <span>
                {payment.postedAt
                  ? `${t('payments.voucher.postedAt', { defaultValue: 'Posted' })}: ${formatDate(payment.postedAt, { locale })}`
                  : ''}
              </span>
            </div>
            <span className="paper-strip" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scoped paper styles — same paper language as the journal voucher: always
   white with dark text; only the band/accents follow the active palette. */
const CSS = `
.pv { min-height: 100vh; display: flex; flex-direction: column; background: var(--color-bg-subtle); color: var(--color-fg); }
.pv-bar { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  min-height: 56px; padding: 8px 20px; border-bottom: 1px solid var(--color-border); background: var(--color-bg); }
.pv-back { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 12px; border-radius: 8px;
  color: var(--color-fg-muted); font-size: 13px; font-weight: 600; border: 1px solid var(--color-border); background: var(--color-bg-subtle); }
.pv-back:hover { color: var(--color-fg); }
[dir="rtl"] .pv-back svg { transform: scaleX(-1); }
.pv-title { font-weight: 800; font-size: 14px; }
.pv-spacer { flex: 1; }
.pv-print { display: inline-flex; align-items: center; gap: 7px; height: 36px; padding: 0 14px; border-radius: 8px;
  border: 0; background: var(--color-brand); color: var(--color-brand-contrast); font-size: 13px; font-weight: 700; cursor: pointer; }
.pv-stage { flex: 1; overflow: auto; padding: 32px 20px 64px; display: flex; flex-direction: column; align-items: center; }

/* A5 landscape: 210mm x 148mm ≈ 794px x 559px */
.a5 { width: 794px; max-width: 100%; min-height: 559px; background: #fff; color: #1a2230;
  box-shadow: 0 30px 80px -20px rgba(0,0,0,.45), 0 8px 24px -8px rgba(0,0,0,.3); border-radius: 3px;
  position: relative; overflow: hidden; display: flex; flex-direction: column; }

.stamp { position: absolute; top: 150px; inset-inline-end: 54px; z-index: 5; font-size: 40px; font-weight: 900;
  letter-spacing: 4px; text-transform: uppercase; padding: 6px 20px; border: 5px solid currentColor; border-radius: 12px;
  transform: rotate(-12deg); opacity: .12; pointer-events: none; white-space: nowrap; color: #AE3A33; }
[dir="rtl"] .stamp { transform: rotate(12deg); letter-spacing: 0; }

.pv-top { padding: 14px 26px 12px; color: #fff; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
  background: linear-gradient(135deg, var(--paper-brand), color-mix(in oklab, var(--paper-brand) 58%, #07071c));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pv-top .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
.pv-top .mk { width: 44px; height: 44px; border-radius: 11px; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.24);
  display: grid; place-items: center; flex: none; font-weight: 800; font-size: 15px; letter-spacing: .04em; }
.pv-top .co { font-size: 18px; font-weight: 800; letter-spacing: -.02em; line-height: 1.15; }
.pv-top .cosub { font-size: 11px; opacity: .82; margin-top: 2px; }
.pv-top .doc { text-align: end; flex: none; }
.pv-top .doc .dt { font-size: 20px; font-weight: 800; letter-spacing: .02em; text-transform: uppercase; }
[dir="rtl"] .pv-top .doc .dt { letter-spacing: 0; }
.pv-top .doc .dn { font-size: 12px; opacity: .9; margin-top: 3px; font-variant-numeric: tabular-nums; }

.pv-meta { display: flex; align-items: center; gap: 26px; padding: 7px 26px; border-bottom: 1px solid #ece8de; flex-wrap: wrap; }
.pv-meta .mi .k { font-size: 9px; color: #888FA5; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
[dir="rtl"] .pv-meta .mi .k { letter-spacing: 0; }
.pv-meta .mi .v { font-size: 12.5px; font-weight: 700; margin-top: 1px; color: #1a2230; font-variant-numeric: tabular-nums; }
.pv-meta .chip { margin-inline-start: auto; font-size: 10px; font-weight: 800; padding: 4px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: .04em; }
[dir="rtl"] .pv-meta .chip { letter-spacing: 0; }
.pv-meta .chip.ok { background: #E4F4EC; color: #1F7A52; }
.pv-meta .chip.bad { background: #FBE7E5; color: #AE3A33; }
.pv-meta .chip.mid { background: #EEF1F8; color: #545B72; }

.pv-body { padding: 12px 26px 0; display: flex; flex-direction: column; gap: 9px; flex: 1; }
.pv-main { display: flex; gap: 20px; align-items: stretch; }
.pv-main .who { flex: 1; min-width: 0; padding: 10px 14px; background: #F4F6FB; border: 1px solid #ece8de; border-radius: 10px; }
.pv-main .k { font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .pv-main .k { letter-spacing: 0; }
.pv-main .who .v { font-size: 17px; font-weight: 800; margin-top: 3px; color: #1a2230; }
.pv-main .who .phone { font-size: 11.5px; font-weight: 700; margin-top: 2px; color: #545B72; font-variant-numeric: tabular-nums; }
.pv-main .who .words-k { margin-top: 8px; }
.pv-main .who .words-v { font-size: 12px; font-weight: 700; margin-top: 2px; color: #1a2230; line-height: 1.45; }

/* Paid now + still owed, as a matched pair. Equal weight on purpose: the
   second number is the one the customer came to hear. */
.pv-main .money { flex: none; width: 262px; display: flex; flex-direction: column; gap: 6px; }
.pv-main .money > * { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;
  padding: 6px 14px; border-radius: 10px; text-align: center; }
.pv-main .amount { border: 1.5px solid var(--paper-brand); }
.pv-main .amount .big { font-size: 22px; font-weight: 900; color: var(--paper-brand); font-variant-numeric: tabular-nums; margin-top: 3px; }
.pv-main .balance.due { border: 1.5px solid #C08A2E; background: #FDF6E9; }
.pv-main .balance.clear { border: 1.5px solid #2F8F63; background: #EDF7F1; }
.pv-main .balance .big { font-size: 20px; font-weight: 900; font-variant-numeric: tabular-nums; margin-top: 3px; }
.pv-main .balance.due .big { color: #96661B; }
.pv-main .balance.clear .big { color: #1F7A52; }
.pv-main .money .sub { font-size: 9px; font-weight: 700; color: #888FA5; margin-top: 3px; line-height: 1.35; }

table.allocs { width: 100%; border-collapse: collapse; }
.allocs thead th { background: var(--paper-brand); color: #fff; font-size: 10px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; padding: 5px 10px; text-align: end; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
[dir="rtl"] .allocs thead th { letter-spacing: 0; }
.allocs thead th.l { text-align: start; } .allocs thead th.idx { text-align: center; width: 30px; }
.allocs thead th:first-child { border-start-start-radius: 8px; }
.allocs thead th:last-child { border-start-end-radius: 8px; }
.allocs tbody td { padding: 5px 10px; font-size: 12px; border-bottom: 1px solid #ece8de; text-align: end; font-variant-numeric: tabular-nums; }
.allocs tbody td.l { text-align: start; }
.allocs tbody tr:nth-child(even) td { background: #F4F6FB; }
.allocs td.idx { color: #888FA5; font-weight: 700; text-align: center; }
.allocs td .acode { font-family: ui-monospace, monospace; font-weight: 700; color: var(--paper-brand); margin-inline-end: 7px; }
.allocs td .aname { color: #545B72; font-weight: 600; }
.allocs td .adate { color: #888FA5; font-weight: 600; margin-inline-start: 7px; font-size: 10.5px; }
.allocs td.amt { font-weight: 700; color: #1a2230; }
/* The document's own position: present for context, so it must not compete
   with the amount this payment actually applied. */
.allocs td.dim { color: #545B72; }
.allocs td.due { font-weight: 700; color: #96661B; }
.allocs td.clear { font-weight: 700; color: #1F7A52; }
/* The rest of the account: a quiet heading row, and tighter rows once there are many. */
.allocs tbody tr.grp td, .allocs tbody tr.grp:nth-child(even) td { background: #fff; text-align: start; padding: 6px 10px 2px;
  font-size: 9px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .allocs tbody tr.grp td { letter-spacing: 0; }
.allocs.many tbody td { padding: 3px 10px; font-size: 11px; }
.allocs tfoot td { padding: 5px 10px; font-size: 12px; text-align: end; font-variant-numeric: tabular-nums;
  border-top: 1.5px solid var(--paper-brand); background: #fff; }
.allocs tfoot td.l { text-align: start; font-size: 9px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .allocs tfoot td.l { letter-spacing: 0; }

.pv-body .blk { padding: 6px 12px; background: #F4F6FB; border-radius: 8px; border: 1px solid #ece8de; }
.pv-body .blk .k { font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #888FA5; }
[dir="rtl"] .pv-body .blk .k { letter-spacing: 0; }
.pv-body .blk .v { font-size: 12px; margin-top: 2px; color: #1a2230; line-height: 1.45; }

.pv-foot { margin-top: auto; flex: none; padding: 8px 26px 0; }
.sigs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 30px; }
.sigs .sig .line { border-top: 1.5px solid #C6CDDC; padding-top: 5px; margin-top: 20px; font-size: 10px; color: #545B72; font-weight: 600; text-align: center; }
.postline { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; margin-top: 4px; font-size: 9.5px; color: #888FA5; border-top: 1px solid #ece8de; }
.paper-strip { display: block; height: 6px; margin: 0 -26px; background: linear-gradient(90deg, var(--paper-brand), var(--paper-accent));
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }

@media print {
  .pv { background: #fff; }
  .pv-bar { display: none !important; }
  .pv-stage { padding: 0; overflow: visible; display: block; }
  .a5 { box-shadow: none; border-radius: 0; width: 100%; min-height: 100vh; }
  /* Taller than the page: shrink the whole paper onto it (see fitToPage). */
  .a5[data-fit] { zoom: var(--fit, 1); min-height: 0; }
  .allocs tr { break-inside: avoid; }
}
`;
